/**
 * Auth manager implementation
 * Retrieves temporary API keys from Carousel Auth System using JWT tokens
 * Architecture: Connects to Keywords AI Gateway and other API adapters
 */
export class SimpleAuthManager {
    constructor() {
        this.keyCache = new Map();
        this.apiBaseUrl = 'https://app.carouseltech.io';
        this.expiryBufferMs = 3 * 60 * 1000; // 3 minutes buffer before expiry (like working Python implementation)
    }
    /**
     * Get temporary Keywords AI API key
     * Architecture: Calls Carousel Auth System to retrieve API credentials
     * Uses actual expiry_date from server response instead of hardcoded duration
     */
    async getTempKeywordsKey(jwtToken) {
        if (!jwtToken) {
            throw new Error("JWT token required for Keywords API");
        }
        const cacheKey = `keywords:${jwtToken}`;
        const cached = this.keyCache.get(cacheKey);
        // Check if key exists and not expiring within buffer time (3 minutes)
        const now = Date.now();
        if (cached && (cached.expiresAt - this.expiryBufferMs) > now) {
            return cached.key;
        }
        try {
            // Call Carousel Auth System to get temporary API key
            // Architecture: GET to /keywords/get-temporary-key
            const response = await fetch(`${this.apiBaseUrl}/keywords/get-temporary-key`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${jwtToken}`,
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to get Keywords API key: ${errorText || response.statusText}`);
            }
            const data = await response.json();
            const tempKey = data.api_key || data.key || data.temp_key;
            if (!tempKey) {
                throw new Error('No API key returned from auth service');
            }
            // Use actual expiry_date from server instead of hardcoded duration
            let expiresAt;
            if (data.expiry_date) {
                expiresAt = new Date(data.expiry_date).getTime();
            }
            else if (data.expires_in) {
                expiresAt = Date.now() + (data.expires_in * 1000);
            }
            else {
                expiresAt = Date.now() + 3600000; // Fallback to 1 hour
            }
            // Cache the key with actual expiry
            this.keyCache.set(cacheKey, {
                key: tempKey,
                expiresAt: expiresAt,
            });
            console.log(`[SimpleAuthManager] Retrieved Keywords API key (expires: ${new Date(expiresAt).toISOString()})`);
            return tempKey;
        }
        catch (error) {
            console.error('[SimpleAuthManager] Error getting Keywords API key:', error);
            // Check if this is a CORS error (typically happens when JWT is expired)
            // The server returns 401/403 but without CORS headers, browser blocks it
            const errorMessage = error instanceof Error ? error.message : String(error);
            const isCorsOrNetworkError = errorMessage.includes('Failed to fetch') ||
                errorMessage.includes('NetworkError') ||
                errorMessage.includes('CORS');
            if (isCorsOrNetworkError) {
                // Don't use expired cached key - it will just cause a 403 from Keywords AI
                // Instead, throw a clear error that the session needs to be refreshed
                console.error('[SimpleAuthManager] CORS/Network error - likely JWT token expired. User needs to re-authenticate.');
                throw new Error('Session expired. Please log in again to continue using AI features.');
            }
            // For other errors, still throw (don't fall back to potentially expired keys)
            throw error;
        }
    }
    /**
     * Check if API key is expired or expiring soon (within 3 minute buffer)
     */
    isKeyExpired(provider, jwtToken) {
        const cacheKey = `${provider}:${jwtToken}`;
        const cached = this.keyCache.get(cacheKey);
        if (!cached)
            return true;
        // Consider expired if within buffer time
        return (cached.expiresAt - this.expiryBufferMs) <= Date.now();
    }
    /**
     * Increment usage counter
     * Architecture: Tracks API usage for billing/limits
     */
    async incrementUsage(jwtToken, count) {
        try {
            // Call Carousel Auth System to track usage
            await fetch(`${this.apiBaseUrl}/user/auth/usage/increment`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${jwtToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ count }),
            });
        }
        catch (error) {
            // Don't fail if usage tracking fails
            console.warn('[SimpleAuthManager] Usage tracking failed:', error);
        }
        console.log(`[SimpleAuthManager] Usage incremented by ${count}`);
    }
    /**
     * Clear cache (useful for logout)
     */
    clearCache() {
        this.keyCache.clear();
    }
}
